import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AppAspect, AppRelationship, ID } from '../../types'; // Adjusted path
import { Input } from "../../components/ui/input";
import { X as XIcon, Search as SearchIcon } from 'lucide-react';
import { getNodeFullPathObject, buildSuggestionLabel, PathObject, searchMatches, cn } from '../../lib/utils'; // Adjusted path
// import { searchMatches } from '../../App'; // Commented out

interface Suggestion {
    id: ID;
    displayLabel: string;
    fullPathObject: PathObject;
    originalNode: AppAspect;
}

interface AutocompleteSearchInputProps {
    allNodes: AppAspect[];
    allRelationships: AppRelationship[];
    onPathSelect: (fullPathObject: PathObject) => void;
    onPathClear: () => void;
    debounceDelay?: number;
    placeholder?: string;
    initialLockedItemPath?: PathObject | null; // To potentially restore locked state
    allOriginalNodesForLabel: AppAspect[]; // Pass all original nodes for label building
    allOriginalRelationshipsForLabel: AppRelationship[]; // Pass all original relationships for label building
}

const AutocompleteSearchInput: React.FC<AutocompleteSearchInputProps> = ({
    allNodes,
    allRelationships,
    onPathSelect,
    onPathClear,
    debounceDelay = 300,
    placeholder = "Search and select aspect...",
    initialLockedItemPath = null,
    allOriginalNodesForLabel,
    allOriginalRelationshipsForLabel
}) => {
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [lockedItem, setLockedItem] = useState<{ displayLabel: string; fullPathObject: PathObject } | null>(null);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [isFocused, setIsFocused] = useState(false);
    const suggestionRefs = useRef<(HTMLLIElement | null)[]>([]); // For scrolling

    // Effect to set initial locked item if provided
    useEffect(() => {
        if (initialLockedItemPath && allNodes.length > 0) {
            // Find the node corresponding to the deepest part of initialLockedItemPath
            let targetNodeName = initialLockedItemPath.l3 || initialLockedItemPath.l2 || initialLockedItemPath.l1;
            const node = allNodes.find(n => n.name === targetNodeName);
            if (node) {
                const displayLabel = buildSuggestionLabel(node, allOriginalNodesForLabel, allOriginalRelationshipsForLabel);
                setLockedItem({ displayLabel, fullPathObject: initialLockedItemPath });
            }
        }
    }, [initialLockedItemPath, allNodes, allOriginalNodesForLabel, allOriginalRelationshipsForLabel]);

    const generateSuggestions = useCallback((term: string) => {
        if (!term.trim()) {
            setSuggestions([]);
            setHighlightedIndex(-1);
            return;
        }
        const matchedNodes = allNodes.filter(node => searchMatches(term, node.name));

        const newSuggestions: Suggestion[] = matchedNodes.map(node => {
            const fullPathObject = getNodeFullPathObject(node, allOriginalNodesForLabel, allOriginalRelationshipsForLabel);
            const displayLabel = buildSuggestionLabel(node, allOriginalNodesForLabel, allOriginalRelationshipsForLabel);
            return { id: node.name, displayLabel, fullPathObject, originalNode: node };
        });
        setSuggestions(newSuggestions);
        setIsDropdownVisible(newSuggestions.length > 0);
        setHighlightedIndex(-1); // Reset highlight when suggestions change
        suggestionRefs.current = newSuggestions.map(() => null); // Reset refs array
    }, [allNodes, allOriginalNodesForLabel, allOriginalRelationshipsForLabel]);

    useEffect(() => {
        if (lockedItem || !inputValue.trim()) {
            setSuggestions([]);
            setIsDropdownVisible(false);
            setHighlightedIndex(-1);
            return;
        }
        const handler = setTimeout(() => {
            generateSuggestions(inputValue);
        }, debounceDelay);

        return () => {
            clearTimeout(handler);
        };
    }, [inputValue, lockedItem, debounceDelay, generateSuggestions]);

    useEffect(() => {
        // Scroll to highlighted suggestion
        if (highlightedIndex >= 0 && highlightedIndex < suggestionRefs.current.length) {
            suggestionRefs.current[highlightedIndex]?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
            });
        }
    }, [highlightedIndex]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!lockedItem) {
            setInputValue(event.target.value);
        }
    };

    const handleSuggestionClick = (suggestion: Suggestion) => {
        onPathSelect(suggestion.fullPathObject);
        setLockedItem({ displayLabel: suggestion.displayLabel, fullPathObject: suggestion.fullPathObject });
        setInputValue(''); // Clear input field text
        setSuggestions([]);
        setIsDropdownVisible(false);
        setHighlightedIndex(-1);
    };

    const handleClearLockedItem = () => {
        onPathClear();
        setLockedItem(null);
        setInputValue(''); // Reset input field as well
        setIsDropdownVisible(false);
        setHighlightedIndex(-1);
    };

    const handleInputFocus = () => {
        setIsFocused(true);
        if (inputValue.trim() && suggestions.length > 0 && !lockedItem) {
            setIsDropdownVisible(true);
        }
    };

    const handleInputBlur = () => {
        setIsFocused(false);
        // Delay hiding to allow click on suggestions
        setTimeout(() => {
            if (!document.activeElement || !document.activeElement.closest('.autocomplete-suggestions')) {
                setIsDropdownVisible(false);
            }
        }, 150);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (lockedItem) return; // No keyboard nav if item is locked

        if (event.key === ' ' && !isDropdownVisible) { // Only stop propagation if not actively navigating dropdown
            // If user is just typing a space in the input for search term
            // allow default behavior (typing space) but stop propagation to global handlers
            event.stopPropagation();
            // Do NOT preventDefault, so space is typed
            return;
        }

        if (isDropdownVisible && suggestions.length > 0) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setHighlightedIndex(prev => (prev + 1) % suggestions.length);
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setHighlightedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
            } else if (event.key === 'Enter') {
                event.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
                    handleSuggestionClick(suggestions[highlightedIndex]);
                }
            } else if (event.key === 'Escape') {
                event.preventDefault();
                setIsDropdownVisible(false);
                setHighlightedIndex(-1);
                setInputValue(''); // Optionally clear input on escape
            }
        } else if (event.key === 'ArrowDown' && inputValue.trim() && suggestions.length === 0 && !isDropdownVisible) {
            // If user presses ArrowDown and dropdown is not visible but could be (has input),
            // try to generate and show suggestions
            generateSuggestions(inputValue);
            if (suggestions.length > 0) setIsDropdownVisible(true);
        }
    };

    return (
        <div className={cn(
            "relative w-full max-w-md transition-all duration-300",
            isFocused ? 'scale-[1.02]' : '',
            "z-40" // High z-index to ensure it stays above other elements
        )}>
            {/* Background gradient blur effect */}
            <div
                className={`
                    absolute inset-0 rounded-xl bg-gradient-to-r from-blue-200/30 to-purple-200/30 
                    backdrop-blur-sm opacity-60 -z-10 
                    ${isFocused ? 'blur-md' : 'blur-sm'} 
                    transition-all duration-300
                `}
            />

            <div className="relative group">
                {/* Focus/hover effect border gradient */}
                <div className={`
                    absolute -inset-0.5 rounded-xl 
                    bg-gradient-to-r from-blue-400 to-purple-500 
                    opacity-0 group-hover:opacity-30 ${isFocused ? 'opacity-70' : ''}
                    blur-sm group-hover:blur-md transition-all duration-300
                `}></div>

                <div className="relative flex items-center">
                    {!lockedItem && (
                        <SearchIcon className={`
                            absolute left-3.5 top-1/2 -translate-y-1/2
                            h-4 w-4 text-muted-foreground
                            transition-all duration-300 ease-in-out
                            ${isFocused ? 'text-blue-500 scale-110' : ''} 
                            ${inputValue ? 'text-blue-400' : ''}
                        `} />
                    )}

                    <Input
                        type="text"
                        placeholder={lockedItem ? '' : placeholder}
                        value={lockedItem ? lockedItem.displayLabel : inputValue}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        onKeyDown={handleKeyDown}
                        disabled={!!lockedItem}
                        className={`
                            pl-10 pr-10 py-3 h-12 
                            rounded-xl shadow-md 
                            border-0 
                            bg-white/90 dark:bg-gray-900/90 
                            backdrop-blur-sm
                            focus:shadow-blue-300/30 dark:focus:shadow-blue-500/20 
                            focus:shadow-lg
                            transition-all duration-300
                            font-medium
                            ${lockedItem ? 'cursor-default text-blue-600 font-semibold' : ''}
                        `}
                    />

                    {lockedItem && (
                        <div className="absolute right-3.5 top-1/2 -translate-y-1/2 flex">
                            <button
                                onClick={handleClearLockedItem}
                                className="flex items-center justify-center h-6 w-6 bg-blue-100/70 hover:bg-blue-200/80 text-blue-600 rounded-full transition-colors duration-200"
                                aria-label="Clear selection"
                            >
                                <XIcon className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {isDropdownVisible && !lockedItem && suggestions.length > 0 && (
                <ul className="autocomplete-suggestions">
                    {suggestions.map((suggestion, index) => (
                        <li
                            key={suggestion.id}
                            ref={el => suggestionRefs.current[index] = el}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleSuggestionClick(suggestion);
                            }}
                            className={cn(
                                "autocomplete-suggestion",
                                index === highlightedIndex && "selected"
                            )}
                        >
                            <div className="flex items-center">
                                <SearchIcon className="h-3.5 w-3.5 mr-2 text-blue-400/60" />
                                <span>{suggestion.displayLabel}</span>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default AutocompleteSearchInput; 